import React, { useCallback, useEffect, FC, useMemo, useState } from "react";
import { Modal, ModalBody, Row, Alert, Col, Button } from "reactstrap";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import { Icon } from "../../../components/Component";
import FormSelect from "../../form-components/SelectComponent";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { nanoid } from "nanoid";
import supplierApis from "../../../api/master/supplier";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import inventoryCreation from "../../../api/inventory/inventoryCreation";
import brandMasterApis from "../../../api/master/brandMaster";


interface SelectOptions {
  label: string;
  value: any;
}

const SEGMENT_TO_WORK_LEVEL: Record<string, string> = {
  Tower: "TOWER_BLOCK",
  "Tower/Block": "TOWER_BLOCK",
  "Tower / Block": "TOWER_BLOCK",
  "Tower/ Block": "TOWER_BLOCK",
  Block: "TOWER_BLOCK",
  Villa: "VILLA",
  Floor: "FLOOR",
  Flat: "FLAT",
};

const WORK_LEVEL_TO_SEGMENT: Record<string, string> = {
  TOWER_BLOCK: "Tower",
  VILLA: "Villa",
  FLOOR: "Floor",
  FLAT: "Flat",
};

const WORK_LEVEL_OPTIONS: SelectOptions[] = [
  { label: "Tower / Block", value: "TOWER_BLOCK" },
  { label: "Villa", value: "VILLA" },
  { label: "Floor", value: "FLOOR" },
  { label: "Flat", value: "FLAT" },
];

const MaterialModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  materialCategoryOptions,
  materialsList,
  editIndex,
}) => {
  const { watch: watchContext, getValues: getParentValues } = useFormContext();
  const orderWorkLevelSetting = watchContext("workLevelSetting");
  const parentTowerId = watchContext("towerBlockVillaId");
  const parentFloorNo = watchContext("floorNo");
  const parentFlatId = watchContext("flatId");
  const parentLocationId = watchContext("locationId");
  const parentSegmentLevel = watchContext("segmentLevel");
  const parentSiteId = watchContext("siteId");
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [quantityAvailable, setQuantityAvailable] = useState<number | null>(null);
  const [stockUom, setStockUom] = useState<string | null>(null);
  const [primaryUomName, setPrimaryUomName] = useState<string | null>(null);
  const [makeOptions, setMakeOptions] = useState<SelectOptions[]>([]);

  const [towerOptions, setTowerOptions] = useState<SelectOptions[]>([]);
  const [floorOptions, setFloorOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);
  const [isTowerLoading, setIsTowerLoading] = useState(false);
  const [isFloorLoading, setIsFloorLoading] = useState(false);
  const [isFlatLoading, setIsFlatLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [towerFloorCounts, setTowerFloorCounts] = useState<Record<string, number>>({});

  const methods = useForm({
    mode: "onChange",
  });
  const { handleSubmit, watch, setValue } = methods;

  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const workLevelSettingLocal = watch("workLevelSetting");
  const normalizedOrderWorkLevel =
    orderWorkLevelSetting && orderWorkLevelSetting !== "NONE"
      ? orderWorkLevelSetting
      : null;
  const normalizedLocalWorkLevel =
    workLevelSettingLocal && workLevelSettingLocal !== "NONE"
      ? workLevelSettingLocal
      : null;
  const workLevelSetting =
    normalizedLocalWorkLevel ?? normalizedOrderWorkLevel ?? null;
  const towerBlockVillaIdWatch = watch("towerBlockVillaId");
  const floorNoWatch = watch("floorNo");
  const flatIdWatch = watch("flatId");
  const locationIdWatch = watch("locationId");
  const materialNameWatch = watch("materialName");

  const resolvedSiteId = useMemo(() => {
    const localSiteId = watch("siteId");
    return localSiteId ?? parentSiteId ?? getParentValues("siteId") ?? null;
  }, [getParentValues, parentSiteId, watch]);

  const shouldShowHierarchy = Boolean(parentSegmentLevel && resolvedSiteId);

  const hierarchyFieldLabel = useMemo(() => {
    switch (workLevelSetting) {
      case "VILLA":
        return "Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Tower / Block";
      default:
        return "Tower / Block / Villa";
    }
  }, [workLevelSetting]);

  const hierarchyFieldPlaceholder = useMemo(() => {
    switch (workLevelSetting) {
      case "VILLA":
        return "Select Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Select Tower / Block";
      default:
        return "Select Tower / Block / Villa";
    }
  }, [workLevelSetting]);

  const handleWorkLevelChange = useCallback(
    (rawValue: any) => {
      const value =
        rawValue && rawValue !== "NONE" ? (rawValue as string) : null;
      setValue("workLevelSetting", value);
      if (!value) {
        setValue("towerBlockVillaId", null);
        setValue("floorNo", null);
        setValue("flatId", null);
        setValue("locationId", null);
        setTowerOptions([]);
        setFloorOptions([]);
        setFlatOptions([]);
        setLocationOptions([]);
        return;
      }
      if (value === "TOWER_BLOCK" || value === "VILLA") {
        setValue("floorNo", null);
        setValue("flatId", null);
        setFloorOptions([]);
        setFlatOptions([]);
      }
      if (value === "FLOOR") {
        setValue("flatId", null);
        setFlatOptions([]);
      }
    },
    [setValue]
  );

  const loadTowerOptions = useCallback(async () => {
    if (!shouldShowHierarchy) {
      setTowerOptions([]);
      return;
    }
    setIsTowerLoading(true);
    try {
      const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
        siteId: resolvedSiteId,
      });
      if (response?.status && Array.isArray(response.data)) {
        setTowerOptions(
          response.data.map((unit: any) => ({
            value: unit.id,
            label: unit.unitName,
          }))
        );
      } else {
        setTowerOptions([]);
      }
    } catch (error) {
      console.log("Failed to load tower options", error);
      setTowerOptions([]);
    } finally {
      setIsTowerLoading(false);
    }
  }, [resolvedSiteId, shouldShowHierarchy]);

  const loadFloorOptions = useCallback(
    async (unitId?: number | string) => {
      if (
        !shouldShowHierarchy ||
        !unitId ||
        (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT")
      ) {
        setFloorOptions([]);
        return;
      }
      setIsFloorLoading(true);
      try {
        const response =
          await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
            siteId: resolvedSiteId,
            unitId,
          });
        if (response?.status && Array.isArray(response.data)) {
          const floors = response.data.map((floor: any) => ({
            value: floor.floorNo,
            label: floor.floorNo,
          }));
          setFloorOptions(floors);

          // Cache floor count for this tower
          setTowerFloorCounts(prev => ({
            ...prev,
            [String(unitId)]: floors.length
          }));
        } else {
          setFloorOptions([]);
        }
      } catch (error) {
        console.log("Failed to load floor options", error);
        setFloorOptions([]);
      } finally {
        setIsFloorLoading(false);
      }
    },
    [resolvedSiteId, shouldShowHierarchy, workLevelSetting]
  );

  const loadFlatOptions = useCallback(
    async (unitId?: number | string, floorNo?: string | null) => {
      if (!shouldShowHierarchy || workLevelSetting !== "FLAT" || !unitId) {
        setFlatOptions([]);
        return;
      }
      setIsFlatLoading(true);
      try {
        const response = await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
          siteId: resolvedSiteId,
          unitId,
          floorNo: floorNo ?? undefined,
        });
        if (response?.status && Array.isArray(response.data)) {
          setFlatOptions(
            response.data.map((flat: any) => ({
              value: flat.id,
              label: flat.subUnitName || flat.unitName,
            }))
          );
        } else {
          setFlatOptions([]);
        }
      } catch (error) {
        console.log("Failed to load flat options", error);
        setFlatOptions([]);
      } finally {
        setIsFlatLoading(false);
      }
    },
    [resolvedSiteId, shouldShowHierarchy, workLevelSetting]
  );

  const loadLocationOptions = useCallback(async () => {
    if (!shouldShowHierarchy || !resolvedSiteId) {
      setLocationOptions([]);
      return;
    }
    setIsLocationLoading(true);
    try {
      const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
        siteId: resolvedSiteId,
      });
      if (response?.status && Array.isArray(response.data)) {
        const options = response.data.map((location: any) => ({
          value: location.id,
          label: location.locationName,
        }));
        setLocationOptions(options);
        const existingValue = getParentValues("locationId");
        if (
          existingValue &&
          !options.some(
            (option: SelectOptions) => option.value == existingValue
          )
        ) {
          setValue("locationId", null);
        }
      } else {
        setLocationOptions([]);
      }
    } catch (error) {
      console.log("Failed to load location options", error);
      setLocationOptions([]);
    } finally {
      setIsLocationLoading(false);
    }
  }, [getParentValues, resolvedSiteId, setValue, shouldShowHierarchy]);

  useEffect(() => {
    if (!modal) {
      setQuantityAvailable(null);
      setStockUom(null);
      setPrimaryUomName(null);
      setMakeOptions([]);
      return;
    }
    if (shouldShowHierarchy) {
      loadTowerOptions();
      loadLocationOptions();
    } else {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      setLocationOptions([]);
      setValue("towerBlockVillaId", null);
      setValue("floorNo", null);
      setValue("flatId", null);
      setValue("locationId", null);
    }
  }, [
    loadLocationOptions,
    loadTowerOptions,
    modal,
    setValue,
    shouldShowHierarchy,
  ]);

  useEffect(() => {
    if (!modal || !shouldShowHierarchy) {
      return;
    }
    if (
      (workLevelSetting === "TOWER_BLOCK" ||
        workLevelSetting === "VILLA" ||
        workLevelSetting === "FLOOR" ||
        workLevelSetting === "FLAT") &&
      towerBlockVillaIdWatch
    ) {
      loadFloorOptions(towerBlockVillaIdWatch);
    } else {
      setFloorOptions([]);
      // Only clear floorNo if workLevelSetting doesn't require it
      if (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT") {
        setValue("floorNo", null);
      }
    }
  }, [
    loadFloorOptions,
    modal,
    setValue,
    shouldShowHierarchy,
    towerBlockVillaIdWatch,
    workLevelSetting,
  ]);

  useEffect(() => {
    if (!modal || !shouldShowHierarchy) {
      return;
    }
    if (workLevelSetting === "FLAT" && towerBlockVillaIdWatch) {
      loadFlatOptions(towerBlockVillaIdWatch, floorNoWatch ?? null);
    } else if (workLevelSetting !== "FLAT") {
      setFlatOptions([]);
      setValue("flatId", null);
    }
  }, [
    floorNoWatch,
    loadFlatOptions,
    modal,
    setValue,
    shouldShowHierarchy,
    towerBlockVillaIdWatch,
    workLevelSetting,
  ]);

  const fetchMakeOptions = async (materialId: any) => {
    if (!materialId) {
      setMakeOptions([]);
      return;
    }
    try {
      const response = await brandMasterApis.fetchBrandById(materialId);
      if (response?.status && response?.data?.brands) {
        const options = response.data.brands
          .filter((brand: any) => brand.status?.toUpperCase() === "ACTIVE")
          .map((brand: any) => ({
            label: brand.make,
            value: brand.id,
          }));
        setMakeOptions(options);
      } else {
        setMakeOptions([]);
      }
    } catch (error) {
      console.log("Failed to fetch make options", error);
      setMakeOptions([]);
    }
  };

  const fetchMaterialStock = async (materialId: any) => {
    if (!resolvedSiteId || !materialId) {
      setQuantityAvailable(null);
      setStockUom(null);
      return;
    }
    try {
      const response = await inventoryCreation.fetchMaterialStock(
        resolvedSiteId,
        materialId
      );
      console.log("Material Stock Response:", response);
      if (response?.status && response?.response) {
        const stockData = response.response;
        setQuantityAvailable(stockData.quantityAvailable ?? null);
        // UOM will be determined from the material data or selected UOM
        setStockUom(null);
      } else {
        setQuantityAvailable(null);
        setStockUom(null);
      }
    } catch (error) {
      console.log("Failed to fetch material stock", error);
      setQuantityAvailable(null);
      setStockUom(null);
    }
  };


  const fetchUom = async (materialId: any, materialData: any, uomId: any) => {
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setValue("uomId", uomId ? uomId : materialUom?.primaryUomId);
      setUomOptions(_uomOptions);
      setPrimaryUomName(materialUom?.PrimaryUomName || null);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, materialId: any) => {
    try {
      const sid = resolvedSiteId;
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId,
        sid
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));
        setMaterialOptions(materialOption);
        setMaterialData(data);
        setValue("materialName", materialId);
        return data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    console.log("formData.item", formData.item);
    if (modal && formData.item) {
      console.log("formData.item", formData.item);
      const {
        materialCategory,
        materialName,
        quantity,
        specification,
        remarks,
        description,
        uomId,
        workLevelSetting: itemWorkLevelSetting,
        towerBlockVillaId,
        floorNo,
        flatId,
        locationId,
        segmentLevel,
        siteId,
        siteStructureUnitId,
        siteStructureSubUnitId,
      } = formData.item;

      // Use siteStructureUnitId as fallback for towerBlockVillaId
      const resolvedTowerBlockVillaId = towerBlockVillaId ?? siteStructureUnitId ?? null;
      // Use siteStructureSubUnitId as fallback for flatId
      const resolvedFlatId = flatId ?? siteStructureSubUnitId ?? null;

      setValue("materialCategory", materialCategory);
      (async () => {
        const _materialData = await fetchMaterials(
          materialCategory,
          materialName
        );
        await Promise.all([
          fetchUom(materialName, _materialData, uomId),
          fetchMaterialStock(materialName),
          fetchMakeOptions(materialName),
        ]);
        if (formData.item?.makeInfo?.length > 0) {
          setValue("make", formData.item.makeInfo.map((m: any) => Number(m.makeId)));
        }
      })();
      setValue("quantity", quantity);
      setValue("specification", specification);
      setValue("remarks", remarks);
      setValue("description", description);

      // Use current order workLevelSetting (from parent form) instead of item's old workLevelSetting
      // This ensures that when Site Hierarchy changes in General Details, the modal shows correct fields
      const resolvedItemWorkLevelSetting =
        normalizedOrderWorkLevel ||
        (itemWorkLevelSetting &&
          itemWorkLevelSetting !== "NONE" &&
          itemWorkLevelSetting) ||
        (segmentLevel ? SEGMENT_TO_WORK_LEVEL[segmentLevel] ?? null : null) ||
        null;

      setValue("workLevelSetting", resolvedItemWorkLevelSetting);
      handleWorkLevelChange(resolvedItemWorkLevelSetting);

      // Load tower options first, then set values after options are loaded
      if (resolvedItemWorkLevelSetting && siteId) {
        loadTowerOptions().then(() => {
          // After tower options are loaded, set the hierarchy field values
          // This ensures the dropdown has options when the value is set
          if (normalizedOrderWorkLevel) {
            // Use current order workLevelSetting - only set values if compatible
            if (
              normalizedOrderWorkLevel === "TOWER_BLOCK" ||
              normalizedOrderWorkLevel === "VILLA"
            ) {
              // Only Tower/Block or Villa - no Floor or Flat
              setValue(
                "towerBlockVillaId",
                resolvedTowerBlockVillaId ?? parentTowerId ?? null
              );
              setValue("floorNo", null);
              setValue("flatId", null);
            } else if (normalizedOrderWorkLevel === "FLOOR") {
              // Tower/Block + Floor required
              setValue(
                "towerBlockVillaId",
                resolvedTowerBlockVillaId ?? parentTowerId ?? null
              );
              setValue("floorNo", floorNo ?? parentFloorNo ?? null);
              setValue("flatId", null);
            } else if (normalizedOrderWorkLevel === "FLAT") {
              // Tower/Block + Floor + Flat all required
              setValue(
                "towerBlockVillaId",
                resolvedTowerBlockVillaId ?? parentTowerId ?? null
              );
              setValue("floorNo", floorNo ?? parentFloorNo ?? null);
              setValue("flatId", resolvedFlatId ?? parentFlatId ?? null);
            }
            // Location is always optional
            setValue("locationId", locationId ?? parentLocationId ?? null);
          } else {
            // Fallback to item values if no order workLevelSetting
            setValue(
              "towerBlockVillaId",
              resolvedTowerBlockVillaId ?? parentTowerId ?? null
            );
            setValue("floorNo", floorNo ?? parentFloorNo ?? null);
            setValue("flatId", resolvedFlatId ?? parentFlatId ?? null);
            setValue("locationId", locationId ?? parentLocationId ?? null);
          }
        });
        loadLocationOptions();
      } else {
        // If no hierarchy, set values immediately
        if (normalizedOrderWorkLevel) {
          if (
            normalizedOrderWorkLevel === "TOWER_BLOCK" ||
            normalizedOrderWorkLevel === "VILLA"
          ) {
            setValue(
              "towerBlockVillaId",
              resolvedTowerBlockVillaId ?? parentTowerId ?? null
            );
            setValue("floorNo", null);
            setValue("flatId", null);
          } else if (normalizedOrderWorkLevel === "FLOOR") {
            setValue(
              "towerBlockVillaId",
              resolvedTowerBlockVillaId ?? parentTowerId ?? null
            );
            setValue("floorNo", floorNo ?? parentFloorNo ?? null);
            setValue("flatId", null);
          } else if (normalizedOrderWorkLevel === "FLAT") {
            setValue(
              "towerBlockVillaId",
              resolvedTowerBlockVillaId ?? parentTowerId ?? null
            );
            setValue("floorNo", floorNo ?? parentFloorNo ?? null);
            setValue("flatId", resolvedFlatId ?? parentFlatId ?? null);
          }
          setValue("locationId", locationId ?? parentLocationId ?? null);
        } else {
          setValue(
            "towerBlockVillaId",
            resolvedTowerBlockVillaId ?? parentTowerId ?? null
          );
          setValue("floorNo", floorNo ?? parentFloorNo ?? null);
          setValue("flatId", resolvedFlatId ?? parentFlatId ?? null);
          setValue("locationId", locationId ?? parentLocationId ?? null);
        }
      }

      // After setting values, load dependent options if needed
      if (
        resolvedItemWorkLevelSetting === "FLOOR" ||
        resolvedItemWorkLevelSetting === "FLAT"
      ) {
        const towerId = resolvedTowerBlockVillaId ?? parentTowerId;
        if (towerId) {
          loadFloorOptions(towerId);
        }
      }
      if (resolvedItemWorkLevelSetting === "FLAT") {
        const towerId = resolvedTowerBlockVillaId ?? parentTowerId;
        const floor = floorNo ?? parentFloorNo;
        if (towerId) {
          loadFlatOptions(towerId, floor);
        }
      }
    } else if (modal) {
      setValue("workLevelSetting", normalizedOrderWorkLevel);
      handleWorkLevelChange(normalizedOrderWorkLevel);
      setValue(
        "towerBlockVillaId",
        parentTowerId ?? getParentValues("towerBlockVillaId") ?? null
      );
      setValue("floorNo", parentFloorNo ?? getParentValues("floorNo") ?? null);
      setValue("flatId", parentFlatId ?? getParentValues("flatId") ?? null);
      setValue(
        "locationId",
        parentLocationId ?? getParentValues("locationId") ?? null
      );
    }
  }, [
    formData.index,
    formData.item,
    loadLocationOptions,
    loadTowerOptions,
    materialCategoryOptions,
    modal,
    getParentValues,
    orderWorkLevelSetting,
    parentFlatId,
    parentFloorNo,
    parentLocationId,
    parentSegmentLevel,
    parentTowerId,
    resolvedSiteId,
    setValue,
    handleWorkLevelChange,
  ]);

  // Helper to get floor count for a tower (from cache or fetch)
  const getFloorCountForTower = useCallback(
    async (towerId: number | string): Promise<number> => {
      const towerIdStr = String(towerId);

      // Return cached value if available
      if (towerFloorCounts[towerIdStr] !== undefined) {
        return towerFloorCounts[towerIdStr];
      }

      // Fetch from API if not cached
      try {
        const response = await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
          siteId: resolvedSiteId,
          unitId: towerId,
        });
        if (response?.status && Array.isArray(response.data)) {
          const count = response.data.length;
          setTowerFloorCounts(prev => ({
            ...prev,
            [towerIdStr]: count
          }));
          return count;
        }
      } catch (error) {
        console.log("Failed to fetch floor count for tower", towerId, error);
      }
      return 0;
    },
    [resolvedSiteId, towerFloorCounts]
  );

  // Pre-fetch floor counts for towers with floor-level entries
  useEffect(() => {
    if (
      !materialsList ||
      !Array.isArray(materialsList) ||
      (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT")
    ) {
      return;
    }

    // Collect all towers that have floor-level entries
    const towersWithFloors = new Set<string>();
    materialsList.forEach((item: any) => {
      if (item.towerBlockVillaId && item.floorNo != null && item.floorNo !== "") {
        towersWithFloors.add(String(item.towerBlockVillaId));
      }
    });

    // Fetch floor counts for towers not yet cached
    towersWithFloors.forEach(async (towerId) => {
      if (towerFloorCounts[towerId] === undefined) {
        await getFloorCountForTower(towerId);
      }
    });
  }, [materialsList, workLevelSetting, towerFloorCounts, getFloorCountForTower]);

  const towerLabel = useMemo(() => {
    const selected = towerOptions.find(
      (option) => option.value == towerBlockVillaIdWatch
    );
    return selected?.label ?? "";
  }, [towerBlockVillaIdWatch, towerOptions]);

  const floorLabel = useMemo(() => {
    return floorNoWatch != null && floorNoWatch !== "" ? floorNoWatch : "";
  }, [floorNoWatch]);

  const flatLabel = useMemo(() => {
    const selected = flatOptions.find((option) => option.value == flatIdWatch);
    return selected?.label ?? "";
  }, [flatIdWatch, flatOptions]);

  const locationLabel = useMemo(() => {
    const selected = locationOptions.find(
      (option) => option.value == locationIdWatch
    );
    return selected?.label ?? "";
  }, [locationIdWatch, locationOptions]);

  // Helper to check if a flat value is present
  const hasFlat = (item: any) =>
    item?.flatId != null &&
    item?.flatId !== "" &&
    item?.flatId !== "null" &&
    item?.flatId !== "undefined";

  // Filter tower options: hide towers where the same material is already added
  const filteredTowerOptions = useMemo(() => {
    if (!materialNameWatch) return towerOptions;
    const existingItems = Array.isArray(materialsList) ? materialsList : [];
    const editIndex =
      formData.action === "edit" && formData.index != null
        ? formData.index
        : -1;

    const blockedTowers = new Set<any>();

    existingItems.forEach((item: any, idx: number) => {
      if (idx === editIndex) return; // Skip current item being edited
      if (item.materialName != materialNameWatch) return; // Different material

      // Block tower/villa at different work levels
      if (
        workLevelSetting === "TOWER_BLOCK" ||
        workLevelSetting === "VILLA"
      ) {
        // TOWER/VILLA level: block if same material + same tower exists
        blockedTowers.add(item.towerBlockVillaId);
      } else if (workLevelSetting === "FLOOR") {
        // FLOOR level: only block tower if tower-level entry exists (no floor specified)
        // This means material was added at tower level, covering ALL floors
        if (!item.floorNo || item.floorNo === "") {
          blockedTowers.add(item.towerBlockVillaId);
        }
        // If floor IS specified → same tower is still available for other floors ✅
      }
    });

    // NEW: Hierarchical progression validation for FLOOR work level
    // For FLOOR level: block tower if all floors are consumed
    // For FLAT level: do NOT block tower based on floor counts,
    // because individual floors may still have available flats
    if (workLevelSetting === "FLOOR") {
      towerOptions.forEach((towerOption) => {
        const towerId = towerOption.value;

        // Rule 1: Check if ANY floor-level entries exist for this material+tower
        // If yes, prevent adding tower-level entry (hierarchy consistency)
        const hasFloorLevelEntries = existingItems.some(
          (item: any, idx: number) =>
            idx !== editIndex &&
            item.materialName == materialNameWatch &&
            item.towerBlockVillaId == towerId &&
            item.floorNo != null &&
            item.floorNo !== ""
        );

        if (hasFloorLevelEntries) {
          // Count how many unique floors are used for this material+tower
          const usedFloors = new Set<string>();
          existingItems.forEach((item: any, idx: number) => {
            if (
              idx !== editIndex &&
              item.materialName == materialNameWatch &&
              item.towerBlockVillaId == towerId &&
              item.floorNo != null &&
              item.floorNo !== ""
            ) {
              usedFloors.add(item.floorNo);
            }
          });

          // Rule 2: Block tower if all floors are consumed
          // Check cached floor count for this tower
          const totalFloorsAvailable = towerFloorCounts[String(towerId)];
          if (totalFloorsAvailable !== undefined && usedFloors.size >= totalFloorsAvailable) {
            // All floors for this tower are used for this material
            // Block the tower completely (can't add more)
            blockedTowers.add(towerId);
          }
          // Note: If floor count not cached yet, tower remains available
          // It will be blocked after user selects tower and floors are loaded
        }
      });
    }

    return towerOptions.filter((opt) => !blockedTowers.has(opt.value));
  }, [
    towerOptions,
    materialNameWatch,
    materialsList,
    formData,
    workLevelSetting,
    towerFloorCounts,
  ]);

  // Filter floor options based on hierarchy level
  const filteredFloorOptions = useMemo(() => {
    if (!materialNameWatch || !towerBlockVillaIdWatch) return floorOptions;
    const existingItems = Array.isArray(materialsList) ? materialsList : [];
    const editIndex =
      formData.action === "edit" && formData.index != null
        ? formData.index
        : -1;

    const blockedFloors = new Set<any>();
    existingItems.forEach((item: any, idx: number) => {
      if (idx === editIndex) return;
      if (
        item.materialName != materialNameWatch ||
        item.towerBlockVillaId != towerBlockVillaIdWatch
      ) return;

      // Block floor in these cases:
      // 1. FLOOR level: if same material + tower + floor exists
      // 2. FLAT level: if floor-level entry exists (no flat specified)
      // Note: Floor is now optional, only block if it has a value

      if (workLevelSetting === "FLOOR") {
        // Block floor if it exists with this material at FLOOR or FLAT level
        if (item.floorNo != null && item.floorNo !== "") {
          blockedFloors.add(item.floorNo);
        }
      } else if (workLevelSetting === "FLAT") {
        // Block floor if floor-level entry exists (covers all flats)
        if (item.floorNo != null && item.floorNo !== "" && !hasFlat(item)) {
          blockedFloors.add(item.floorNo);
        }
      }
    });

    return floorOptions.filter(
      (opt) => !blockedFloors.has(opt.value)
    );
  }, [
    floorOptions,
    materialNameWatch,
    towerBlockVillaIdWatch,
    materialsList,
    formData,
    workLevelSetting,
  ]);

  // Filter flat options: hide flats where the same material is already added for that flat
  const filteredFlatOptions = useMemo(() => {
    if (!materialNameWatch || !towerBlockVillaIdWatch) return flatOptions;
    const existingItems = Array.isArray(materialsList) ? materialsList : [];
    const editIndex =
      formData.action === "edit" && formData.index != null
        ? formData.index
        : -1;

    // Check if this material is already added at floor level for this tower+floor
    const isFloorLevelBlocked = existingItems.some(
      (item: any, idx: number) => {
        if (idx === editIndex) return false;
        return (
          item.materialName == materialNameWatch &&
          item.towerBlockVillaId == towerBlockVillaIdWatch &&
          item.floorNo == floorNoWatch &&
          !hasFlat(item)
        );
      }
    );

    // If floor level already has this material, all flats are blocked
    if (isFloorLevelBlocked) return [];

    // Otherwise, filter out specific flats that already have this material
    const blockedFlats = new Set<any>();
    existingItems.forEach((item: any, idx: number) => {
      if (idx === editIndex) return;
      if (
        item.materialName == materialNameWatch &&
        item.towerBlockVillaId == towerBlockVillaIdWatch &&
        item.floorNo == floorNoWatch &&
        hasFlat(item)
      ) {
        blockedFlats.add(item.flatId);
      }
    });

    return flatOptions.filter(
      (opt) => !blockedFlats.has(opt.value)
    );
  }, [
    flatOptions,
    materialNameWatch,
    towerBlockVillaIdWatch,
    floorNoWatch,
    materialsList,
    formData,
  ]);

  const submitForm = (data: any) => {
    const {
      materialCategory,
      materialName,
      hsnCode,
      quantity,
      specification,
      remarks,
      description,
      uomId,
      make,
      workLevelSetting: workLevelValue,
      towerBlockVillaId: towerIdValue,
      floorNo: floorNoValue,
      flatId: flatIdValue,
      locationId: locationIdValue,
    } = data;

    const materialLabel = materialOptions.find(
      (item) => item.value == materialName
    )?.label;
    const categoryLabel = materialCategoryOptions.find(
      (item: any) => item.value == materialCategory
    )?.label;
    const uomLabel = uomOptions.find((item) => item.value == uomId)?.label;
    const selectedMakeIds = Array.isArray(make) ? make : [];
    const makeInfoData = selectedMakeIds
      .map((id: any) => ({
        makeId: Number(id),
        makeName: makeOptions.find((item) => item.value == id)?.label || "",
      }))
      .filter((m: any) => m.makeId && m.makeName);

    const resolvedWorkLevelSetting =
      (workLevelValue && workLevelValue !== "NONE"
        ? workLevelValue
        : normalizedOrderWorkLevel) ?? null;
    const resolvedSegmentLevel = resolvedWorkLevelSetting
      ? WORK_LEVEL_TO_SEGMENT[resolvedWorkLevelSetting] ?? null
      : parentSegmentLevel ?? null;

    const hierarchyData = {
      workLevelSetting: resolvedWorkLevelSetting,
      segmentLevel: resolvedSegmentLevel,
      towerBlockVillaId: towerIdValue ?? null,
      siteStructureUnitId: towerIdValue ?? null,
      towerBlockVillaLabel: towerLabel,
      unitName: towerLabel,
      floorNo: floorNoValue ?? null,
      floorLabel,
      flatId: flatIdValue ?? null,
      siteStructureSubUnitId: flatIdValue ?? null,
      flatLabel,
      subUnitName: flatLabel,
      locationId: locationIdValue ?? null,
      locationLabel,
      locationName: locationLabel,
    };

    // Hierarchy-aware duplicate validation
    const existingItems = Array.isArray(materialsList) ? materialsList : [];
    const editIndex =
      formData.action === "edit" && formData.index != null
        ? formData.index
        : -1;

    let duplicateMessage = "";

    if (shouldShowHierarchy) {
      const newHasFlat = hasFlat({ flatId: flatIdValue });

      for (let idx = 0; idx < existingItems.length; idx++) {
        if (idx === editIndex) continue;
        const item: any = existingItems[idx];
        if (item.materialName != materialName) continue;
        if (item.locationId != (locationIdValue ?? null)) continue;

        // VILLA / TOWER_BLOCK: same tower/villa = duplicate
        if (workLevelSetting === "VILLA" || workLevelSetting === "TOWER_BLOCK") {
          if (item.towerBlockVillaId == (towerIdValue ?? null)) {
            duplicateMessage = "This material already exists for the selected site hierarchy.";
            break;
          }
          continue;
        }

        // FLOOR / FLAT flow
        if (workLevelSetting === "FLOOR" || workLevelSetting === "FLAT") {
          if (
            item.towerBlockVillaId != (towerIdValue ?? null) ||
            item.floorNo != (floorNoValue ?? null)
          ) continue;

          const existingHasFlat = hasFlat(item);

          // New item has NO flat → applies to all flats in that floor
          if (!newHasFlat) {
            duplicateMessage = existingHasFlat
              ? "Material already exists in a flat on this floor. Cannot add at floor level."
              : "This material already exists for the selected site hierarchy.";
            break;
          }

          // New item HAS flat
          if (!existingHasFlat) {
            duplicateMessage =
              "Material already added at floor level, which covers all flats.";
            break;
          }
          if (item.flatId == (flatIdValue ?? null)) {
            duplicateMessage = "This material already exists for the selected site hierarchy.";
            break;
          }
        }
      }
    } else {
      // Without site hierarchy: duplicate = same materialName + same materialCategory
      const isDuplicate = existingItems.some((item: any, idx: number) => {
        if (idx === editIndex) return false;
        return (
          item.materialName == materialName &&
          item.materialCategory == materialCategory
        );
      });
      if (isDuplicate) {
        duplicateMessage = "This material already exists for the selected category.";
      }
    }

    if (duplicateMessage) {
      setAlertInfo({
        type: "error",
        message: duplicateMessage,
        errors: [],
      });
      return;
    }

    if (formData.action == "add" || formData.index == null) {
      append({
        key: nanoid(),
        materialCategory,
        categoryLabel,
        materialName,
        materialLabel,
        hsnCode,
        quantity,
        specification,
        remarks,
        uomId,
        uomLabel,
        makeInfo: makeInfoData,
        description,
        ...hierarchyData,
      });
    } else {
      update(formData.index, {
        key: nanoid(),
        materialCategory,
        categoryLabel,
        materialName,
        materialLabel,
        hsnCode,
        quantity,
        specification,
        remarks,
        uomId,
        uomLabel,
        makeInfo: makeInfoData,
        description,
        ...hierarchyData,
      });
    }
    closeModal();
  };

  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl">
      <ModalBody>
        <h5 className="title">
          {formData.action == "add" || formData.index == null
            ? "Add New Line"
            : "Update Line"}
        </h5>
        <div style={{ marginTop: "15px" }}></div>
        {alertInfo.message && (
          <div className="mb-3 mt-1">
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="alert-icon"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={
                  alertInfo.type === "error" ? "alert-circle" : "check-circle"
                }
              />
            </Alert>
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row className="mt-12">
              <div className="col-md-4">
                <FormSelect
                  name="materialCategory"
                  label="Material Category"
                  required={true}
                  options={materialCategoryOptions}
                  placeholder="Select Material Category"
                  onChange={(e: any) => {
                    fetchMaterials(e, null);
                    setQuantityAvailable(null);
                    setStockUom(null);
                    setPrimaryUomName(null);
                    setMakeOptions([]);
                    setValue("make", []);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormSelect
                  name="materialName"
                  label="Material Name"
                  required={true}
                  options={materialOptions}
                  placeholder="Select Material Name"
                  onChange={async (e: any) => {
                    setValue("make", []);
                    await Promise.all([
                      fetchUom(e, materialData, null),
                      fetchMaterialStock(e),
                      fetchMakeOptions(e),
                    ]);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormMultiSelect
                  name="make"
                  label="Make"
                  options={makeOptions}
                  placeholder="Select Make"
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-5">
                <FormInput
                  type="number"
                  min={0.1}
                  required={true}
                  name="quantity"
                  label={
                    <span>
                      Required Quantity
                      {quantityAvailable !== null && quantityAvailable !== undefined && (
                        <span
                          style={{
                            color: "#52c41a",
                            marginLeft: "8px",
                            fontWeight: "normal",
                          }}
                        >
                          (Qyt Avl: {Number(quantityAvailable).toFixed(2)}{" "}
                          {uomOptions.find((opt) => opt.value === watch("uomId"))?.label || primaryUomName || ""})
                        </span>
                      )}
                    </span>
                  }
                  placeholder="Enter Quantity"
                />
              </div>
              <div className="col-md-3">
                <FormSelect
                  name="uomId"
                  label="UOM"
                  required={true}
                  options={uomOptions}
                  placeholder="Units"
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  type="text"
                  name="specification"
                  label="Specification"
                  placeholder="Enter Specification"
                />
              </div>
            </Row>
            {shouldShowHierarchy && (
              <Row className="mt-4">
                <div className="col-md-3">
                  <FormSelect
                    name="towerBlockVillaId"
                    label={hierarchyFieldLabel}
                    options={filteredTowerOptions}
                    placeholder={hierarchyFieldPlaceholder}
                    required={
                      workLevelSetting === "TOWER_BLOCK" ||
                      workLevelSetting === "VILLA" ||
                      workLevelSetting === "FLOOR" ||
                      workLevelSetting === "FLAT"
                    }
                    isLoading={isTowerLoading}
                  />
                </div>
                 {(workLevelSetting === "FLOOR" ||
                  workLevelSetting === "FLAT") && (
                  <div className="col-md-3">
                    <FormSelect
                      name="floorNo"
                      label="Floor"
                      options={filteredFloorOptions}
                      placeholder="Select Floor"
                      required={false}
                      isLoading={isFloorLoading}
                    />
                  </div>
                )}
                {workLevelSetting === "FLAT" && (
                  <div className="col-md-3">
                    <FormSelect
                      name="flatId"
                      label="Flat"
                      options={filteredFlatOptions}
                      placeholder="Select Flat"
                      required={workLevelSetting === "FLAT" && parentSegmentLevel !== "Flat"}
                      isLoading={isFlatLoading}
                    />
                  </div>
                )}
                {workLevelSetting === "FLOOR" && (
                  <div className="col-md-3">
                    <FormSelect
                      name="locationId"
                      label="Location"
                      options={locationOptions}
                      placeholder="Select Location"
                      isLoading={isLocationLoading}
                    />
                  </div>
                )}
                {workLevelSetting !== "FLOOR" &&
                  workLevelSetting !== "FLAT" && (
                    <div className="col-md-3">
                      <FormSelect
                        name="locationId"
                        label="Location"
                        options={locationOptions}
                        placeholder="Select Location"
                        isLoading={isLocationLoading}
                      />
                    </div>
                  )}
              </Row>
            )}
            {shouldShowHierarchy && workLevelSetting === "FLAT" && (
              <Row className="mt-4">
                <div className="col-md-3">
                  <FormSelect
                    name="locationId"
                    label="Location"
                    options={locationOptions}
                    placeholder="Select Location"
                    isLoading={isLocationLoading}
                  />
                </div>
              </Row>
            )}
            <Row className="mt-4">
              <div className="col-md-6">
                <FormInput
                  type="text"
                  required={false}
                  min={1}
                  name="remarks"
                  label="Remarks (if any)"
                  placeholder="Enter Remarks"
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  type="text"
                  name="description"
                  label="Description (if any)"
                  placeholder="Enter Description"
                  maxLength={500}
                />
              </div>
            </Row>
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  {formData.action === "edit" && formData.index != null && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Update
                      </Button>
                    </li>
                  )}
                  {(formData.action == "add" || formData.index == null) && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Save
                      </Button>
                    </li>
                  )}
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default MaterialModal;
